<template>
    <div>
        <header>
            <v-container>
                <v-row no-gutters>
                    <h1 class="font-weight-light headline">
                        {{ $t("title.licenses") }}
                    </h1>
                    <v-spacer></v-spacer>
                    <v-lazy v-if="isLicensesLoaded" transition="fade-transition">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="searchLabel"
                            single-line
                            dense
                            hide-details
                            style="max-width: 200px"
                            class="mr-5 pr-5"
                        ></v-text-field>
                    </v-lazy>
                    <v-lazy transition="fade-transition">
                        <v-btn
                            v-if="$store.state.tokenData.user.user.tenant_id == '1'"
                            color="success"
                            dark
                            small
                            @click.stop="openNewLicenseForm"
                        >{{ $t("general.add") }}</v-btn>
                    </v-lazy>
                </v-row>
            </v-container>
        </header>
        <section class="py-7">
            <v-container>
                <v-sheet class="px-3 pt-3 pb-3" v-if="!isLicensesLoaded">
                    <v-skeleton-loader
                        class="mx-auto"
                        type="table"
                    ></v-skeleton-loader>
                </v-sheet>
                <v-card v-else>
                    <v-data-table
                        :headers="headers"
                        :items="licenses"
                        :search="search"
                    >
                        <template v-slot:item.action="{ item }">
                            <div v-if="$store.state.tokenData.user.user.tenant_id == '1'" style="display: flex">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }" >
                                        <v-icon
                                            v-tr-class-on-hover="'blue'"
                                            size="20px"
                                            color="blue"
                                            class="mr-2"
                                            @click="editLicenseItem(item)"
                                            v-on="on"
                                        >mdi-pencil-outline</v-icon>
                                    </template>
                                    <span>{{ $t("general.edit") }}</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }" >
                                        <v-icon
                                            size="22px"
                                            color="blue"
                                            v-tr-class-on-hover="'blue'"
                                            class="mr-2"
                                            @click="editLicenceDeviceItem(item)"
                                            v-on="on"
                                        >mdi-eye-plus</v-icon>
                                    </template>
                                    <span>{{ $t("licenses.manage_devices") }}</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }" >
                                        <v-icon
                                            size="22px"
                                            color="red"
                                            v-tr-class-on-hover="'red'"
                                            class="mr-2"
                                            @click="confirmDelete(item)"
                                            v-on="on"
                                        >mdi-delete-outline</v-icon>
                                    </template>
                                    <span>{{ $t("general.delete") }}</span>
                                </v-tooltip>
                            </div>
                            <div v-else>
                                    <v-tooltip top>
                                    <template v-slot:activator="{ on }" >
                                        <v-icon
                                            size="22px"
                                            color="blue"
                                            v-tr-class-on-hover="'blue'"
                                            class="mr-2"
                                            @click="editLicenceDeviceItem(item)"
                                            v-on="on"
                                        >mdi-eye-plus</v-icon>
                                    </template>
                                    <span>{{ $t("licenses.manage_devices") }}</span>
                                </v-tooltip>
                            </div>
                        </template>
                        <template v-slot:item.is_active="{ item }">
                            <v-switch v-if="$store.state.tokenData.user.user.tenant_id == '1'"
                                class="mt-0 pt-0 justify-content-center"
                                hide-details
                                color="green"
                                v-model="item.is_active"
                                @change="changeStatus(item)"
                            ></v-switch>
                            <span v-else :class="item.is_active ? 'green--text' : 'red--text'">
                             {{ item.is_active ? 'Aktivní' : 'Neaktivní' }}
                           </span>
                        </template>
                        <template v-slot:item.name="{ item }">
                            {{ item.name }}
                        </template>
                        <template v-slot:item.client="{ item }">
                            {{ item.name_client }}
                        </template>
                        <template v-slot:item.devices="{ item }">
                            {{ item.devices }}
                        </template>
                        <template v-slot:item.valid_from="{ item }">
                            {{ formatDate(item.valid_from) }}
                        </template>
                        <template v-slot:item.valid_to="{ item }">
                            {{ formatDate(item.valid_to) }}
                        </template>
                        <template v-slot:item.created_at="{ item }">
                            {{ formatDate(item.created_at) }} 
                        </template>

                        <template v-slot:item.oculus="{ item }">
                            <div v-if="item.oculusDevices && item.oculusDevices.length">
                                <v-chip
                                    v-for="oculus in item.oculusDevices"
                                    :key="oculus.id"
                                    small
                                    class="mr-1 mb-1"
                                >
                                    {{ oculus.code }}
                                </v-chip>
                            </div>
                            <span v-else>
                                {{ $t('general.empty') }}
                            </span>
                        </template>
                        
                        <template v-slot:item.modules="{ item }">
                            <div v-if="item.modules && item.modules.length">
                                <v-chip
                                    v-for="module in item.modules"
                                    :key="module.id"
                                    small
                                    class="mr-1 mb-1"
                                >
                                    {{ module.name }}
                                </v-chip>
                            </div>
                            <span v-else>
                                {{ $t('general.empty') }}
                            </span>
                        </template>

                    </v-data-table>
                </v-card>
                
                <!-- License Form Dialog -->
                <LicenseForm
                    :license="selectedLicense"
                    :visible="dialogL.licenseForm"
                    :isEdited="isLicenseEdited"
                    @addLicense="addLicenseToList"
                    @updateLicense="updateLicenseInList"
                    @close="dialogL.licenseForm = false"
                    @snackMessage="snackMessage"
                    @refresh="getLicenses" 
                />
                
                <!-- License Device Form Dialog -->
                <!-- <LicenceDeviceForm
                    :license="selectedLicense"
                    :visible="dialogL.LicenceDeviceForm"
                    :isEdited="isLicenseDeviceEdited"
                    @close="closeLicenceDeviceForm"
                    @snackMessage="snackMessage"
                    @refresh="getLicenses"
                /> -->
                  
                <LicenceDeviceForm
                   :device-data="selectedLicense"
                  :visible="dialogL.LicenceDeviceForm"
                  @close="closeLicenceDeviceForm"
                  @snackMessage="snackMessage"
                  @refresh="getLicenses"
                />


                <!-- Confirmation Dialog -->
                <ConfirmationDialog
                    :name="selectedLicense.name"
                    :text="'licenses.delete_confirmation'"
                    v-if="dialogL.confirmation"
                    @confirm="deleteLicense"
                    @cancel="cancelDelete"
                />

                <SnackMessage ref="SnackMessage" />
            </v-container>
        </section>
    </div>
</template>

<script>
import LicenseForm from "../components/Licences/LicenseForm";
import LicenceDeviceForm from "../components/Licences/LicenceFormDevices";
import ConfirmationDialog from "../components/ConfirmationDialog";
import SnackMessage from "../components/SnackMessage";

export default {
    data() {
        return {
            licenses: [],
            isLicensesLoaded: false,
            search: "",
            dialogL: {
                confirmation: false,
                licenseForm: false,
                LicenceDeviceForm: false,
            },
            selectedLicense: {},
            isLicenseEdited: false,
            isLicenseDeviceEdited: false,
        };
    },

    components: {
        LicenseForm,
        ConfirmationDialog,
        SnackMessage,
        LicenceDeviceForm,
    },

    computed: {
        headers() {
            if(this.$store.state.tokenData.user.user.tenant_id === "1") {
                return [
                    {
                        text: this.$t("general.actions"),
                        value: "action",
                        sortable: false,
                        width: "150px" 
                    },
                    {
                        text: this.$t("licenses.is_active"),
                        align: "left",
                        sortable: false,
                        value: "is_active",
                    },
                    {
                        text: this.$t("licenses.license"),
                        align: "left",
                        sortable: false,
                        value: "name",
                    },
                    {
                        text: this.$t("licenses.client"),
                        align: "left",
                        sortable: false, 
                        value: "client",
                    },
                    { 
                        text: this.$t("licenses.modules"),
                        align: "left",
                        sortable: false, 
                        value: "modules",
                    },
                    {
                        text: this.$t("licenses.oculus"),
                        align: "left",
                        sortable: false, 
                        value: "oculus",
                    },
                    {
                        text: this.$t("licenses.valid_from"),
                        align: "left",
                        sortable: false,
                        value: "valid_from",
                    },
                    {
                        text: this.$t("licenses.valid_to"),
                        align: "left",
                        sortable: false,
                        value: "valid_to",
                    },
                    {
                        text: this.$t("licenses.devices"),
                        align: "left",
                        sortable: false,
                        value: "devices",
                    },
                ];
            } else {
                return [
                    {
                        text: this.$t("general.actions"),
                        value: "action",
                        sortable: false,
                        width: "150px" 
                    },
                    {
                        text: this.$t("licenses.is_active"),
                        align: "left",
                        sortable: false,
                        value: "is_active",
                    },
                    {
                        text: this.$t("licenses.license"),
                        align: "left",
                        sortable: false,
                        value: "name",
                    },
                    { 
                        text: this.$t("licenses.modules"),
                        align: "left",
                        sortable: false, 
                        value: "modules",
                    },
                    {
                        text: this.$t("licenses.oculus"),
                        align: "left",
                        sortable: false, 
                        value: "oculus",
                    },
                    {
                        text: this.$t("licenses.valid_from"),
                        align: "left",
                        sortable: false,
                        value: "valid_from",
                    },
                    {
                        text: this.$t("licenses.valid_to"),
                        align: "left",
                        sortable: false,
                        value: "valid_to",
                    },
                    {
                        text: this.$t("licenses.devices"),
                        align: "left",
                        sortable: false,
                        value: "devices",
                    }
                ];
            }
        },
        
        searchLabel() {
            return this.$t("general.search");
        },
    },

    created() {
        this.getLicenses();
    },

    methods: {
        async changeStatus(item) {
            try { 
                const request = await axios.put(
                    "/license/status/" + item.id,
                    item,
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                        },
                    }
                );
                this.getLicenses();
                this.snackMessage("general.item_saved", "success");
            } catch (error) {
                console.error(error);
                this.snackMessage("general.error_saving", "error");
            }
        },
        snackMessage(msg, type) {
            if (this.$refs.SnackMessage) {
                this.$refs.SnackMessage.showMessage(this.$t(msg), type); 
            }
        },
        
        formatDate(dateString) {
            if (!dateString) {
                return '';
            }
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.error("date string error:", dateString);
                return 'invalid date'; 
            }
            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const formattedDay = String(day).padStart(2, '0');
            const formattedMonth = String(month).padStart(2, '0');
            const formattedHours = String(hours).padStart(2, '0');
            const formattedMinutes = String(minutes).padStart(2, '0');
            return `${formattedDay}.${formattedMonth}.${year} ${formattedHours}:${formattedMinutes}`;
        },
        
        async getLicenses() {
            try {
                const response = await axios.get("/licensesList", {
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                this.licenses = response.data;
            } catch (error) {
                console.error("Error fetching licenses:", error.response?.data || error);
                this.snackMessage("general.error_loading_data", "error");
            } finally {
                this.isLicensesLoaded = true;
            }
        },
        
        openNewLicenseForm() {
            this.selectedLicense = {};
            this.isLicenseEdited = false;
            this.dialogL.licenseForm = true;
        },
        
        addLicenseToList(newLicense) {
            this.licenses.unshift(newLicense); 
        },
        
        updateLicenseInList(updatedLicense) {
            const index = this.licenses.findIndex((item) => item.id == updatedLicense.id);
            if (index !== -1) {
                this.licenses.splice(index, 1, updatedLicense);
            }
        },
        
        confirmDelete(item) {
            this.selectedLicense = item; 
            this.dialogL.confirmation = true;
        },
        
        editLicenseItem(item) { 
            this.selectedLicense = { ...item };
            this.isLicenseEdited = true;
            this.dialogL.licenseForm = true;
        },
        
       editLicenceDeviceItem(item) {
       let editL = this.licenses.filter(e => e.id == item.id);
       let deviceData = {
        id: editL[0].id,
        name: editL[0].name,
        devices: editL[0].devices,
        codes: editL[0].oculusDevices
       }
       this.selectedLicense = deviceData;
       this.isLicenseDeviceEdited = true;
       this.dialogL.LicenceDeviceForm = true;
}, 
        
        closeLicenceDeviceForm() {
            this.dialogL.LicenceDeviceForm = false;
            this.selectedLicense = {};
            this.isLicenseDeviceEdited = false;
        },
        
        cancelDelete() {
            this.dialogL.confirmation = false;
            this.selectedLicense = {}; 
        },
        
        async deleteLicense() {
            this.dialogL.confirmation = false;
            if (!this.selectedLicense || !this.selectedLicense.id) return;

            const licenseIdToDelete = this.selectedLicense.id;

            try {
                await axios.delete(`/deleteLicense/${licenseIdToDelete}`, { 
                    headers: {
                        Authorization: "Bearer " + this.$store.state.tokenData.user.access_token,
                    },
                });
                this.licenses = this.licenses.filter(lic => lic.id !== licenseIdToDelete);
                this.snackMessage("licenses.deleted_successfully", "success");
            } catch (error) {
                console.error("Error deleting license:", error.response?.data || error);
                this.snackMessage("general.error_deleting", "error");
            } finally {
                this.selectedLicense = {}; 
            }
        },
    },
};
</script>